import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Plus, Trash } from '@/core/ui/icons';
import { useState, type ChangeEvent, type FocusEvent, type ReactNode } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch,
  type FieldPath,
  type UseFormReturn,
} from 'react-hook-form';
import { errorMessage, isApiError } from '@/core/http';
import { Badge, Button, Card, Dialog, Field, Input, Page, Select, Textarea, type BadgeTone } from '@/core/ui';
import { useDeleteProduct, useProductStatus, useSaveProduct } from '../application/queries';
import {
  BRANDS,
  brandLabel,
  collapseSpaces,
  digitsOnly,
  emptyProductForm,
  LIMITS,
  PACKAGINGS,
  productFormSchema,
  publishBlockers,
  STATUS_LABELS,
  tidyText,
  toProductBody,
  toProductForm,
  type Product,
  type ProductForm,
} from '../domain/product';

const TONES: Record<string, BadgeTone> = { draft: 'warning', published: 'success', archived: 'neutral' };

/** Campo de imagem injetado pelo backoffice (contexto media). */
export type ProductImageSlot = (props: {
  url: string | null;
  onChange: (value: { key: string; url: string } | null) => void;
}) => ReactNode;

interface Props {
  product?: Product;
  canEdit: boolean;
  imageSlot: ProductImageSlot;
  onBack: () => void;
  onSaved: (product: Product) => void;
  onDeleted: () => void;
}

type Form = UseFormReturn<ProductForm>;
type Format = { on: 'change' | 'blur'; apply: (v: string) => string };
const tidy: Format = { on: 'blur', apply: collapseSpaces };

/**
 * Registra o campo com formatação: `change` reescreve enquanto se digita
 * (ordem), `blur` arruma ao sair (espaços, linhas em branco).
 */
function formatted(form: Form, name: FieldPath<ProductForm>, fmt: Format) {
  const reg = form.register(name);
  const set = (v: string) =>
    form.setValue(name, fmt.apply(v) as never, { shouldDirty: true, shouldValidate: true });
  return {
    ...reg,
    onChange:
      fmt.on === 'change'
        ? async (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => set(e.target.value)
        : reg.onChange,
    onBlur: async (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (fmt.on === 'blur') set(e.target.value);
      await reg.onBlur(e);
    },
  };
}

/** Criação e edição de produto — dados, foto e as abas do portal (descrição, composição, modo de usar). */
export function ProductEditor({ product, canEdit, imageSlot, onBack, onSaved, onDeleted }: Props) {
  const save = useSaveProduct();
  const form = useForm<ProductForm>({
    resolver: zodResolver(productFormSchema),
    defaultValues: product ? toProductForm(product) : emptyProductForm(),
    mode: 'onTouched',
  });
  const { errors, isDirty } = form.formState;
  const [imageUrl, summary] = useWatch({ control: form.control, name: ['imageUrl', 'summary'] });

  const submit = form.handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync({ id: product?.id, body: toProductBody(values) });
      form.reset(toProductForm(saved));
      onSaved(saved);
    } catch (e) {
      if (isApiError(e) && e.is('duplicate_slug'))
        form.setError('name', { message: 'Já existe um produto com este nome' });
    }
  });

  return (
    <Page>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-fg-subtle hover:text-fg"
      >
        <ArrowLeft aria-hidden className="size-4" />
        Produtos
      </button>

      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-fg sm:text-3xl">
              {product ? product.name : 'Novo produto'}
            </h1>
            {product && (
              <Badge tone={TONES[product.status] ?? 'neutral'}>{STATUS_LABELS[product.status]}</Badge>
            )}
          </div>
          {product && <p className="mt-1 text-base text-fg-subtle">{brandLabel(product.brand)}</p>}
        </div>
        {canEdit && (
          <Button size="lg" onClick={submit} loading={save.isPending} disabled={!!product && !isDirty}>
            {product ? 'Salvar alterações' : 'Criar produto'}
          </Button>
        )}
      </header>

      {save.isError && !(isApiError(save.error) && save.error.is('duplicate_slug')) && (
        <p
          role="alert"
          className="mb-6 rounded-xl border border-line-strong bg-surface px-4 py-3 text-sm text-fg"
        >
          {errorMessage(save.error)}
        </p>
      )}

      <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
        <form onSubmit={submit} noValidate className="min-w-0">
          <fieldset disabled={!canEdit} className="space-y-6">
            <Section title="Produto">
              <Field label="Nome" error={errors.name?.message}>
                {(p) => (
                  <Input
                    {...p}
                    maxLength={LIMITS.name.max}
                    placeholder="Ex.: Guardin 30 Sachês 8g – Sabor Limão"
                    {...formatted(form, 'name', tidy)}
                  />
                )}
              </Field>
              <Field
                label="Resumo"
                error={errors.summary?.message}
                hint={`Uma linha, aparece no card do portal. ${summary.length} de ${LIMITS.summary}.`}
              >
                {(p) => (
                  <Input
                    {...p}
                    maxLength={LIMITS.summary}
                    placeholder="Suplemento em pó para diluir."
                    {...formatted(form, 'summary', tidy)}
                  />
                )}
              </Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Marca">
                  {(p) => (
                    <Select {...p} {...form.register('brand')}>
                      {BRANDS.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Embalagem" hint="Ilustração usada quando não há foto.">
                  {(p) => (
                    <Select {...p} {...form.register('packaging')}>
                      {PACKAGINGS.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Ordem" error={errors.position?.message} hint="Menor aparece antes.">
                  {(p) => (
                    <Input
                      {...p}
                      inputMode="numeric"
                      maxLength={7}
                      {...formatted(form, 'position', { on: 'change', apply: digitsOnly })}
                    />
                  )}
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-fg-muted">
                <input type="checkbox" className="size-4 accent-teal-500" {...form.register('bestSeller')} />
                Mais vendido (aparece primeiro, com selo)
              </label>
            </Section>

            <Section title="Descrição" description="Parágrafos separados por uma linha em branco.">
              <Blocks name="description" form={form} />
            </Section>

            <Section title="Composição" description="Ingredientes e quantidades, na ordem do rótulo.">
              <Composition form={form} />
            </Section>

            <Section title="Modo de usar" description="Recomendações, advertências, conservação…">
              <Blocks name="usage" form={form} />
            </Section>
          </fieldset>
        </form>

        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <Card className="p-5">
            <Controller
              control={form.control}
              name="imageKey"
              render={({ field }) => (
                <>
                  {imageSlot({
                    url: imageUrl,
                    onChange: (v) => {
                      field.onChange(v?.key ?? null);
                      form.setValue('imageUrl', v?.url ?? null, { shouldDirty: true });
                    },
                  })}
                </>
              )}
            />
          </Card>
          {product && canEdit && <Lifecycle product={product} dirty={isDirty} onDeleted={onDeleted} />}
          {!product && (
            <Card className="p-5 text-sm text-fg-muted">
              O produto nasce como <strong className="text-fg">rascunho</strong>. Depois de criado, você o
              publica no portal por aqui.
            </Card>
          )}
        </aside>
      </div>
    </Page>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-5 p-6">
      <div>
        <h2 className="text-lg font-semibold text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-fg-subtle">{description}</p>}
      </div>
      {children}
    </Card>
  );
}

function Blocks({ name, form }: { name: 'description' | 'usage'; form: Form }) {
  const { fields, append, remove } = useFieldArray({ control: form.control, name });
  const errors = form.formState.errors[name];
  const full = fields.length >= LIMITS.blocks;
  return (
    <div className="space-y-4">
      {fields.map((f, i) => {
        const err = errors?.[i];
        return (
          <div key={f.id} className="space-y-2 rounded-xl border border-line p-4">
            <div className="flex items-center gap-2">
              <Input
                aria-label="Título do bloco"
                placeholder={name === 'usage' ? 'Título (opcional) — ex.: Recomendações' : 'Título (opcional)'}
                maxLength={LIMITS.blockTitle}
                {...formatted(form, `${name}.${i}.title`, tidy)}
              />
              <button
                type="button"
                aria-label="Remover bloco"
                onClick={() => remove(i)}
                className="rounded-lg p-2 text-gray-400 hover:bg-surface-2 hover:text-fg"
              >
                <Trash className="size-4" />
              </button>
            </div>
            {err?.title && <p className="text-xs text-fg">{err.title.message}</p>}
            <Textarea
              aria-label="Texto do bloco"
              aria-invalid={err?.text ? true : undefined}
              rows={4}
              {...formatted(form, `${name}.${i}.text`, { on: 'blur', apply: tidyText })}
            />
            {err?.text && <p className="text-xs text-fg">{err.text.message}</p>}
          </div>
        );
      })}
      {errors?.root && <p className="text-xs text-fg">{errors.root.message}</p>}
      <div className="flex items-center gap-3">
        <Button size="sm" variant="secondary" disabled={full} onClick={() => append({ title: '', text: '' })}>
          <Plus aria-hidden className="size-4" />
          Adicionar bloco
        </Button>
        {full && <span className="text-xs text-fg-subtle">Limite de {LIMITS.blocks} blocos.</span>}
      </div>
    </div>
  );
}

function Composition({ form }: { form: Form }) {
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'composition' });
  const errors = form.formState.errors.composition;
  const full = fields.length >= LIMITS.ingredients;
  return (
    <div className="space-y-2">
      {fields.length === 0 && <p className="text-sm text-fg-subtle">Nenhum ingrediente.</p>}
      {fields.map((f, i) => {
        const err = errors?.[i];
        const message = err?.ingredient?.message ?? err?.amount?.message;
        return (
          <div key={f.id}>
            <div className="flex items-center gap-2">
              <Input
                aria-label="Ingrediente"
                aria-invalid={err?.ingredient ? true : undefined}
                placeholder="Ingrediente"
                maxLength={LIMITS.ingredient}
                {...formatted(form, `composition.${i}.ingredient`, tidy)}
              />
              <Input
                aria-label="Quantidade"
                aria-invalid={err?.amount ? true : undefined}
                placeholder="Quantidade"
                className="w-40"
                maxLength={LIMITS.amount}
                {...formatted(form, `composition.${i}.amount`, tidy)}
              />
              <button
                type="button"
                aria-label="Remover ingrediente"
                onClick={() => remove(i)}
                className="rounded-lg p-2 text-gray-400 hover:bg-surface-2 hover:text-fg"
              >
                <Trash className="size-4" />
              </button>
            </div>
            {message && <p className="mt-1 text-xs text-fg">{message}</p>}
          </div>
        );
      })}
      <div className="flex items-center gap-3 pt-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={full}
          onClick={() => append({ ingredient: '', amount: '' })}
        >
          <Plus aria-hidden className="size-4" />
          Adicionar ingrediente
        </Button>
        {full && <span className="text-xs text-fg-subtle">Limite de {LIMITS.ingredients} ingredientes.</span>}
      </div>
    </div>
  );
}

function Lifecycle({
  product,
  dirty,
  onDeleted,
}: {
  product: Product;
  dirty: boolean;
  onDeleted: () => void;
}) {
  const status = useProductStatus();
  const del = useDeleteProduct();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const run = (action: 'publish' | 'unpublish' | 'archive') => status.mutate({ id: product.id, action });
  const blockers = publishBlockers(product);

  return (
    <Card className="space-y-4 p-5">
      <h2 className="text-base font-semibold">Publicação</h2>
      {product.status === 'draft' && (
        <>
          <p className="text-sm text-fg-muted">Rascunho: não aparece no portal.</p>
          {blockers.length > 0 && (
            <ul className="space-y-1 rounded-lg bg-surface-2 px-3 py-2 text-sm text-fg-muted">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
          <Button
            className="w-full"
            disabled={dirty || blockers.length > 0}
            loading={status.isPending}
            onClick={() => run('publish')}
          >
            Publicar no portal
          </Button>
          {dirty && <p className="text-xs text-fg-subtle">Salve as alterações antes de publicar.</p>}
        </>
      )}
      {product.status === 'published' && (
        <>
          <p className="text-sm text-fg-muted">Publicado: visível no portal.</p>
          <Button
            variant="secondary"
            className="w-full"
            loading={status.isPending}
            onClick={() => run('unpublish')}
          >
            Voltar para rascunho
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            loading={status.isPending}
            onClick={() => run('archive')}
          >
            Arquivar
          </Button>
        </>
      )}
      {product.status === 'archived' && (
        <>
          <p className="text-sm text-fg-muted">Arquivado: fora do portal.</p>
          <Button
            variant="secondary"
            className="w-full"
            loading={status.isPending}
            onClick={() => run('unpublish')}
          >
            Restaurar como rascunho
          </Button>
        </>
      )}
      {product.status !== 'published' && (
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="w-full text-center text-xs text-fg hover:underline"
        >
          Apagar produto
        </button>
      )}
      {(status.error ?? del.error) && (
        <p className="text-sm text-fg">{errorMessage(status.error ?? del.error)}</p>
      )}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Apagar o produto?"
        description={product.name}
      >
        <p className="text-sm text-fg-muted">O produto some de vez. Não dá para desfazer.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={del.isPending}
            onClick={() => del.mutate(product.id, { onSuccess: onDeleted })}
          >
            Apagar
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}
